import * as React from "react";
import { useHistory } from "react-router";

import { UserRegistered } from "@/interfaces";
import { AppContext } from "@/providers/AppContextProvider";
import {
  phoneNumberMask,
  phoneRegExp,
  redirectPlayerIfSignUpFinished,
  URLS,
  useIndustryRoles,
  useSocialCoachSession,
} from "@/utils";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CoachService, LoginStepServices, PlayerSignUpStep, SocialCoachSessionService } from "@/services";
import { toast } from "sonner";
import { Button, Card, Combobox, Field, Input } from "@/components/ui";
import { Png } from "@/assets";
import MaskedInput from "react-text-mask";
import { z } from "zod";

const personalInfoSchema = z.object({
  firstName: z.string().trim().min(1, "First Name is required"),
  lastName: z.string().trim().min(1, "Last Name is required"),
  phoneNumber: z.string().regex(phoneRegExp, "Phone number is not valid").min(1, "Phone number is required"),
  industryRole: z.string().optional(),
});

type PersonalInfoValues = z.infer<typeof personalInfoSchema>;

const IntegratedFirstLoginPage: React.FC = () => {
  const history = useHistory();
  const { refreshSession, session } = React.useContext(AppContext);
  const { playerId, coachId } = useSocialCoachSession();

  React.useEffect(() => {
    redirectPlayerIfSignUpFinished(session?.signUpStep);
  }, []);

  const updateContextAndNext = (user: UserRegistered) => {
    refreshSession();
    history.push(URLS.player.firstPicture);
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: playerIndustryRoles, isLoading: isLoadingRoles } = useIndustryRoles();

  const form = useForm<PersonalInfoValues>({
    resolver: zodResolver(personalInfoSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: session?.firstName,
      lastName: session?.lastName,
      phoneNumber: session?.phone,
      industryRole: "",
    },
  });

  React.useEffect(() => {
    if (playerIndustryRoles?.industryRoles?.[0]?.name && !form.getValues("industryRole")) {
      form.setValue("industryRole", playerIndustryRoles.industryRoles[0].name);
    }
  }, [playerIndustryRoles, form]);

  const onSubmit = async (data: PersonalInfoValues) => {
    setIsSubmitting(true);
    try {
      const response = await SocialCoachSessionService.updatePlayer({
        phone: data.phoneNumber,
        firstName: data.firstName,
        lastName: data.lastName,
        photoUrl: session?.photoUrl,
      });

      if (coachId && playerId && data.industryRole) {
        await CoachService.updatePlayerIndustryRole(coachId, playerId, data.industryRole);
      }

      await LoginStepServices.sendPlayerStep(PlayerSignUpStep.PLAYER_PROFILE_DATA);

      refreshSession();
      updateContextAndNext(response);
      history.push(URLS.player.firstPicture);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const industryRoleOptions = React.useMemo(() => {
    return (playerIndustryRoles?.availableIndustryRoles || []).map(el => ({
      label: el.name,
      value: el.name,
    }));
  }, [playerIndustryRoles?.availableIndustryRoles]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-200px)] p-4 sm:p-6">
      <Card className="w-full max-w-[500px] p-6 sm:p-10 shadow-lg border-slate-200">
        <div className="flex flex-col mb-4 sm:mb-6">
          <div className="flex justify-center mb-4 sm:mb-6">
            <img src={Png.Logo} alt="SocialCoach" className="w-48 sm:w-60" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 text-center sm:text-left">Create your profile</h2>
          <p className="text-slate-500 text-center sm:text-left text-sm sm:text-base mt-1 whitespace-pre-line">
            Just a few details to customize your experience.
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="firstName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="First Name" error={fieldState.error?.message}>
                  <Input {...field} disabled={isSubmitting} />
                </Field>
              )}
            />
            <Controller
              name="lastName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Last Name" error={fieldState.error?.message}>
                  <Input {...field} disabled={isSubmitting} />
                </Field>
              )}
            />
          </div>

          <Controller
            name="phoneNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Phone Number" error={fieldState.error?.message}>
                <MaskedInput
                  {...field}
                  mask={phoneNumberMask}
                  disabled={isSubmitting}
                  render={(ref, props) => <Input ref={ref as any} {...props} />}
                />
              </Field>
            )}
          />

          <Controller
            name="industryRole"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Industry Role" error={fieldState.error?.message}>
                <Combobox
                  items={industryRoleOptions}
                  value={industryRoleOptions.find(o => o.value === field.value)}
                  onValueChange={val => field.onChange(val?.value)}
                  disabled={isSubmitting || isLoadingRoles}
                  searchable
                  showClear
                />
              </Field>
            )}
          />

          <Button type="submit" size="lg" className="w-full h-11 sm:h-12 text-base mt-2" loading={isSubmitting}>
            Continue
          </Button>
        </form>
      </Card>
    </div>
  );
};

export const FirstLoginPage = IntegratedFirstLoginPage;
